import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { adminActAsAgencyPath } from "@/routes"

/**
 * OKS-683 — Per-row "Act as {Agency}" entry on `/admin/agencies`. The
 * agency-axis twin of `BrandListActAsButton` (OKS-312).
 *
 * Inertia `<Form method="post">` with a hidden `agency_id` input. Locked
 * contract: FLAT body shape (`{ agency_id: <id> }`); the server reads
 * `params[:agency_id]` directly. No nesting — there's no model.
 *
 * Test IDs (UX Spec §2.1):
 *   - `data-testid="admin-agency-act-as-button"` on the form wrapper.
 *   - `data-agency-id={agency.id}` on the form wrapper so Playwright can
 *     target the row for a specific agency.
 */
interface AgencyListActAsButtonProps {
  agency: {
    id: number
    name: string
  }
}

export function AgencyListActAsButton({ agency }: AgencyListActAsButtonProps) {
  const { t } = useTranslation()
  const label = t("admin.act_as.button.act_as_agency", { agency: agency.name })

  return (
    <Form
      action={adminActAsAgencyPath()}
      method="post"
      className="inline-flex"
      data-testid="admin-agency-act-as-button"
      data-agency-id={agency.id}
    >
      {({ processing }) => (
        <>
          <input type="hidden" name="agency_id" value={agency.id} />
          <Button
            type="submit"
            size="sm"
            disabled={processing}
            aria-label={label}
          >
            {label}
          </Button>
        </>
      )}
    </Form>
  )
}
